/* @flow */
import React, {Component} from 'react';
import {findDOMNode} from 'react-dom';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {Button, ButtonPrimary} from '@homeaway/react-buttons';
import BedroomsFilter from '../BedroomsFilter/BedroomsFilter';
import searchParamsActions from '../../actions/searchParams';
import {cannotFilter} from '../../utils/searchParamsUtils';
import BathroomsFilter from '../BathroomsFilter/BathroomsFilter';
import InstantConfirmationFilter from '../InstantConfirmationFilter/InstantConfirmationFilter';
import keyboardConstants from '../../constants/keyboardConstants';
import {renderFilterGroups} from '../MoreFilters/MoreFilters';
import seeResultsMessage from '../SeeResultsMessage/SeeResultsMessage';
import PriceFilter from '../PriceFilter/PriceFilter';
import {instantBookFilter, newArrayFromFilters} from '../../utils/filters';
import type {BoundsType} from '../PriceFilter/PriceFilter';
import type {ChildrenArray} from 'react';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import type {State as ReduxState} from '../../reducers';
import type {IntlShape} from 'react-intl';
import {triggerEdapEventForCheckbox} from '../../utils/filtersEdapUtils';
import {media} from '../../utils/breakpoints';

import './MoreFiltersPanel.less';

type StateProps = {
    canFit3Columns: boolean,
    canFit4Columns: boolean,
    currency: string,
    filter: string[],
    hasDates: boolean,
    inputMaxBedrooms: ?number,
    inputMaxPrice: ?number,
    inputMinBedrooms: ?number,
    inputMinPrice: ?number,
    inputParamsAreChanged: boolean,
    isMobile: boolean,
    locale: string,
    maxBathrooms: ?number,
    minBathrooms: ?number,
    priceInputValuesAreValid: boolean,
    selectedFilters: string[],
    topBannersHeight: number
};

type InjectIntlProps = {
    intl: IntlShape
};

type ComponentStateProps = {
    moreFiltersContentRef: ?HTMLDivElement,
    moreFiltersPanelRef: ?HTMLDivElement
};

type Props = {
    filterGroups: FilterGroupType[],
    resultsCount?: ?number,
    isVisible?: boolean,
    onApplyFilters: (void) => void,
    onCancel: () => void,
    onInputParamsRangeFilterChange: (SearchParamsType) => void,
    updateInputParams: (SearchParamsType) => void,
    updateFilterCheck: (HTMLInputElement) => void
} & StateProps &
    InjectIntlProps;

export class MoreFiltersPanel extends Component<Props, ComponentStateProps> {
    constructor(props: Props) {
        super(props);
        this.state = {
            moreFiltersContentRef: null,
            moreFiltersPanelRef: null
        };
    }

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
    }

    componentDidUpdate(prevProps: Props) {
        const {topBannersHeight, isVisible} = this.props;
        const node = findDOMNode(this);
        const {moreFiltersContentRef} = this.state;

        /* adjust panel's position */
        if (
            node instanceof Element &&
            node.parentNode &&
            node.parentNode instanceof HTMLElement &&
            (prevProps.topBannersHeight !== topBannersHeight ||
                parseInt(
                    window.getComputedStyle(node.parentNode).marginTop,
                    10
                ) !== topBannersHeight)
        ) {
            node.parentNode.style.setProperty(
                'margin-top',
                `${topBannersHeight}px`
            );
        }

        /* focus content for keyboard input */
        if (!prevProps.isVisible && isVisible && moreFiltersContentRef) {
            /* wait till next cycle to focus to make sure dropdown is open before focusing since
            we cannot focus an element with 0 height */
            setTimeout(() => {
                moreFiltersContentRef.focus();
            }, 0);
        }

        /* If we are hidden, scroll the user back to the top of the panel */
        if (!isVisible && moreFiltersContentRef) {
            moreFiltersContentRef.scrollTop = 0;
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    handleKeyDown = (e: SyntheticKeyboardEvent<*>) => {
        const {isVisible} = this.props;
        if (isVisible && e.which === keyboardConstants.ESC) {
            this.props.onApplyFilters();
        }
    };

    handleMoreFilterCheck = (event: SyntheticEvent<HTMLInputElement>) => {
        if (!event.currentTarget) {
            return;
        }

        triggerEdapEventForCheckbox(event);

        this.props.updateFilterCheck(event.currentTarget);
    };

    getColumns = (children: ChildrenArray<*>, columns: number = 2) => {
        let arr = [];

        for (let i = 0; i < children.length; i++) {
            const modulus = i % columns;
            arr[modulus] = arr[modulus] || [];
            arr[modulus].push(children[i]);
        }

        return arr;
    };

    handlePriceChange = (bounds: BoundsType) => {
        const {updateInputParams} = this.props;
        const values = {
            minPrice: bounds[0] || 0,
            maxPrice: bounds[1]
        };
        updateInputParams(values);
    };

    setMoreFiltersContentRef = (component: ?HTMLDivElement) => {
        this.setState({moreFiltersContentRef: component});
    };

    setMoreFiltersPanelRef = (component: ?HTMLDivElement) => {
        this.setState({moreFiltersPanelRef: component});
    };

    renderPrimaryFilters = (className: string = '') => {
        const {
            currency,
            locale,
            hasDates,
            filter,
            inputMaxPrice,
            inputMinPrice,
            onInputParamsRangeFilterChange,
            inputMinBedrooms,
            inputMaxBedrooms,
            minBathrooms,
            maxBathrooms
        } = this.props;
        const isInstantConfirmationChecked = !!instantBookFilter(filter);
        return (
            <div
                key="primaryFiltersMoreFiltersPanel"
                className={className}
                role="presentation"
            >
                <PriceFilter
                    currency={currency}
                    hasDates={hasDates}
                    onPriceChange={this.handlePriceChange}
                    minPrice={inputMinPrice}
                    maxPrice={inputMaxPrice}
                    className="MoreFiltersPanel__item"
                    locale={locale}
                />
                <BedroomsFilter
                    onChange={onInputParamsRangeFilterChange}
                    minBedrooms={inputMinBedrooms || 0}
                    maxBedrooms={inputMaxBedrooms}
                    className="MoreFiltersPanel__item"
                />
                <BathroomsFilter
                    onChange={onInputParamsRangeFilterChange}
                    minBathrooms={minBathrooms || 0}
                    maxBathrooms={maxBathrooms}
                    className="MoreFiltersPanel__item"
                />
                <InstantConfirmationFilter
                    id="moreFiltersConfirmation__switch"
                    checked={isInstantConfirmationChecked}
                    handleChecked={this.handleMoreFilterCheck}
                    className="MoreFiltersPanel__item"
                />
            </div>
        );
    };

    render() {
        const {
            handleMoreFilterCheck,
            props: {
                canFit3Columns,
                canFit4Columns,
                filter,
                filterGroups,
                intl,
                isMobile,
                onApplyFilters,
                onCancel,
                priceInputValuesAreValid,
                resultsCount,
                selectedFilters
            }
        } = this;

        const submitDisabled =
            cannotFilter(resultsCount) || !priceInputValuesAreValid;

        const cancelLabel = intl.formatMessage({
            id: 'formModalCloseText',
            description: 'cancel button',
            defaultMessage: 'Cancel'
        });

        const inputFilters = newArrayFromFilters({filter});
        const appliedFilters = selectedFilters;

        const childrenToRender = [
            ...renderFilterGroups(appliedFilters, {
                filterGroups,
                className: 'MoreFiltersPanelGroup',
                handleChecked: handleMoreFilterCheck,
                checked: inputFilters,
                tooltipOptions: {
                    isMobile,
                    parentNode: this.state.moreFiltersPanelRef,
                    scrollNode: this.state.moreFiltersContentRef
                }
            })
        ];

        const columns = this.getColumns(
            childrenToRender,
            canFit4Columns ? 3 : 2
        );

        /* If we cannot fit 3 columns, we need to add the primary filters to the filter groups for the 2 columns */
        if (!canFit3Columns && columns[0]) {
            columns[0].unshift(this.renderPrimaryFilters());
        }

        return (
            <div
                role="presentation"
                className="MoreFiltersPanel"
                data-wdio="MoreFiltersPanel"
                ref={this.setMoreFiltersPanelRef}
            >
                <div
                    role="presentation"
                    className="MoreFiltersPanel__content"
                    ref={this.setMoreFiltersContentRef}
                >
                    {canFit3Columns &&
                        this.renderPrimaryFilters(
                            'MoreFiltersPanel__column MoreFiltersPanel__column--left'
                        )}
                    {columns.map((component, index) => (
                        <div
                            role="presentation"
                            className="MoreFiltersPanel__column"
                            key={`MoreFiltersPanel-column-${index}`}
                        >
                            {component}
                        </div>
                    ))}
                </div>
                <div
                    role="presentation"
                    className={'MoreFiltersPanel__buttons--container'}
                >
                    <nav className="MoreFiltersPanel__buttons">
                        <Button
                            id="moreFiltersPanel__btn--cancel"
                            label={cancelLabel}
                            onClick={onCancel}
                        />
                        <ButtonPrimary
                            id="moreFiltersPanel__btn--submit"
                            disabled={submitDisabled}
                            label={seeResultsMessage({
                                intl: this.props.intl,
                                resultsCount
                            })}
                            onClick={onApplyFilters}
                        />
                    </nav>
                </div>
            </div>
        );
    }
}

const mapDispatchToProps = {
    updateInputParams: searchParamsActions.updateInputParams,
    updateFilterCheck: searchParamsActions.updateFilterCheck
};

const mapStateToProps = ({
    searchParams: {
        inputParams: {
            filter,
            minBedrooms,
            maxBedrooms,
            minPrice,
            maxPrice,
            minBathrooms,
            maxBathrooms,
            arrival,
            departure
        },
        inputParamsAreChanged,
        priceInputValuesAreValid,
        selectedFilters
    },
    system: {currency, locale},
    topBanners: {height: topBannersHeight},
    viewport: {breakpoint}
}: ReduxState): StateProps => ({
    currency,
    locale,
    hasDates: !!(arrival && departure),
    isMobile: !media(breakpoint, {minWidth: 'SM_MIN'}),
    canFit3Columns: media(breakpoint, {minWidth: 'MD_MIN'}),
    canFit4Columns: media(breakpoint, {minWidth: 'LG_MIN'}),
    inputMinBedrooms: minBedrooms,
    inputMaxBedrooms: maxBedrooms,
    inputMinPrice: minPrice,
    inputMaxPrice: maxPrice,
    filter: filter || [],
    minBathrooms,
    maxBathrooms,
    inputParamsAreChanged,
    priceInputValuesAreValid,
    selectedFilters,
    topBannersHeight
});

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(MoreFiltersPanel);
